import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { ListConfig, ListType, CompanyColumn, PageColumn, CompanySortField } from './types';

interface ListConfigModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialConfig?: ListConfig;
  onSave: (config: ListConfig) => void;
}

const COMPANY_COLUMNS: Array<{ value: CompanyColumn; label: string }> = [
  { value: 'slug', label: 'Company Name' },
  { value: 'category', label: 'Category' },
  { value: 'employees', label: 'Company Size' },
  { value: 'page_count', label: 'Number of Pages' },
  { value: 'total_plans', label: 'Total Plans' },
  { value: 'total_addons', label: 'Total Add-ons' },
  { value: 'avg_plans_per_page', label: 'Avg Plans per Page' },
  { value: 'has_freemium', label: 'Has Freemium' },
  { value: 'has_trial', label: 'Has Free Trial' },
  { value: 'has_license', label: 'Has License Pricing' },
  { value: 'has_usage', label: 'Has Usage Pricing' },
  { value: 'has_public_pricing', label: 'Has Public Pricing' },
  { value: 'has_contact_pricing', label: 'Requires Contact' },
];

const COMPANY_SORT_OPTIONS: Array<{ value: CompanySortField; label: string }> = [
  { value: 'company_name', label: 'Company Name' },
  { value: 'page_count', label: 'Number of Pages' },
  { value: 'total_plans', label: 'Total Plans' },
  { value: 'total_addons', label: 'Total Add-ons' },
  { value: 'avg_plans_per_page', label: 'Avg Plans per Page' },
];

const PAGE_SORT_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'company_name', label: 'Company Name' },
  { value: 'addon_count', label: 'Add-ons Count' },
  { value: 'plan_count', label: 'Plan Count' },
  { value: 'modality_count', label: 'Modality Count' },
  { value: 'threshold_count', label: 'Threshold Count' },
];

const PAGE_COLUMNS: Array<{ value: PageColumn; label: string }> = [
  { value: 'slug', label: 'Company Name' },
  { value: 'category', label: 'Category' },
  { value: 'employees', label: 'Company Size' },
  { value: 'addon_count', label: 'Add-ons Count' },
  { value: 'plan_count', label: 'Plan Count' },
  { value: 'has_freemium', label: 'Has Freemium' },
  { value: 'has_trial', label: 'Has Free Trial' },
  { value: 'has_license', label: 'Has License Pricing' },
  { value: 'has_usage', label: 'Has Usage Pricing' },
  { value: 'has_public_pricing', label: 'Has Public Pricing' },
  { value: 'has_contact_pricing', label: 'Requires Contact' },
];

export const ListConfigModal: React.FC<ListConfigModalProps> = ({
  open,
  onOpenChange,
  initialConfig,
  onSave,
}) => {
  const [step, setStep] = useState<1 | 2>(1);
  const [listType, setListType] = useState<ListType>('company');
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
  const [sortField, setSortField] = useState<string>('page_count');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Reset when modal opens
  useEffect(() => {
    if (!open) {
      setStep(1);
      return;
    }

    if (initialConfig) {
      setListType(initialConfig.listType);
      setSelectedColumns([...initialConfig.columns]);
      setSortField(initialConfig.sort?.field || 'page_count');
      setSortOrder(initialConfig.sort?.order || 'desc');
      setStep(2); // Start at column selection if editing
    } else {
      setListType('company');
      setSelectedColumns(['slug', 'category', 'employees', 'page_count', 'total_plans']); // Default columns
      setSortField('page_count');
      setSortOrder('desc');
      setStep(1);
    }
  }, [initialConfig, open]);

  const handleListTypeSelect = (type: ListType) => {
    setListType(type);
    // Set default columns and sort based on list type
    if (type === 'company') {
      setSelectedColumns(['slug', 'category', 'employees', 'page_count', 'total_plans']);
      setSortField('page_count');
      setSortOrder('desc');
    } else {
      setSelectedColumns(['slug', 'category', 'addon_count', 'plan_count']);
      setSortField('plan_count');
      setSortOrder('desc');
    }
    setStep(2);
  };

  const handleColumnToggle = (column: string) => {
    setSelectedColumns(prev => {
      if (prev.includes(column)) {
        // Don't allow removing all columns
        if (prev.length === 1) return prev;
        return prev.filter(c => c !== column);
      } else {
        return [...prev, column];
      }
    });
  };

  const handleSave = () => {
    const config: ListConfig = {
      listType,
      columns: selectedColumns as CompanyColumn[] | PageColumn[],
      sort: {
        field: sortField,
        order: sortOrder,
      },
    };
    onSave(config);
  };

  const availableColumns = listType === 'company' ? COMPANY_COLUMNS : PAGE_COLUMNS;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Configure List</DialogTitle>
          <DialogDescription>
            {step === 1
              ? 'Choose whether to list companies or pricing pages'
              : `Select columns to display (${selectedColumns.length} selected)`}
          </DialogDescription>
        </DialogHeader>

        <div className="py-6">
          {/* Step 1: List Type Selection */}
          {step === 1 && (
            <div className="space-y-4">
              <Label className="text-base font-semibold">List Type</Label>
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => handleListTypeSelect('company')}
                  className="group flex flex-col items-center gap-4 p-8 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all"
                >
                  <div className="text-4xl">🏢</div>
                  <div className="text-center">
                    <h3 className="text-lg font-semibold text-gray-900 group-hover:text-blue-900">Companies</h3>
                    <p className="text-sm text-gray-500 mt-1">List companies with aggregated data</p>
                  </div>
                </button>
                <button
                  onClick={() => handleListTypeSelect('page')}
                  className="group flex flex-col items-center gap-4 p-8 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all"
                >
                  <div className="text-4xl">📄</div>
                  <div className="text-center">
                    <h3 className="text-lg font-semibold text-gray-900 group-hover:text-blue-900">Pricing Pages</h3>
                    <p className="text-sm text-gray-500 mt-1">List individual pricing pages</p>
                  </div>
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Column Selection */}
          {step === 2 && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">Configure List</Label>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setStep(1)}
                  className="text-xs"
                >
                  ← Change List Type
                </Button>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-900">
                  {listType === 'company' ? '🏢 Company List' : '📄 Page List'}
                </p>
              </div>

              {/* Sort Options */}
              <div className="space-y-3">
                <Label className="text-sm font-semibold">Sort By</Label>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="sort-field" className="text-xs text-gray-600">Field</Label>
                    <Select value={sortField} onValueChange={setSortField}>
                      <SelectTrigger id="sort-field">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {(listType === 'company' ? COMPANY_SORT_OPTIONS : PAGE_SORT_OPTIONS).map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="sort-order" className="text-xs text-gray-600">Order</Label>
                    <Select value={sortOrder} onValueChange={(v) => setSortOrder(v as 'asc' | 'desc')}>
                      <SelectTrigger id="sort-order">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="asc">Ascending</SelectItem>
                        <SelectItem value="desc">Descending</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Column Selection */}
              <div className="space-y-3">
                <Label className="text-sm font-semibold">Display Columns ({selectedColumns.length} selected)</Label>
                <div className="grid grid-cols-2 gap-3">
                  {availableColumns.map((column) => (
                    <div key={column.value} className="flex items-center space-x-2">
                      <Checkbox
                        id={`column-${column.value}`}
                        checked={selectedColumns.includes(column.value)}
                        onCheckedChange={() => handleColumnToggle(column.value)}
                      />
                      <label
                        htmlFor={`column-${column.value}`}
                        className="text-sm font-normal leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                      >
                        {column.label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          {step === 2 && (
            <Button onClick={handleSave} disabled={selectedColumns.length === 0}>
              Save List
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
